import { observer } from "mobx-react";
import { MdPlaylistAdd } from "react-icons/md";
import { BsCheck } from "react-icons/bs";
import { ActionIcon } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import styles from "./styles/ToggleAddToPlaylistButton.module.css";
import { Button } from "@mantine/core";
import { useStores } from "@/hooks/useStores";

type BtnPosition = "bottom" | "right" | "none";

type ToggleAddToPlaylistButtonProps = {
    episode: Episode;
    podcast: Podcast;
    position?: BtnPosition;
    /** Let a click on a saved episode remove it again */
    removable?: boolean;
};

/**
 * Saving an episode to the list and taking it off again, with the one-time suggestion to sign in after a
 * first save. Shared by the button and by the menu on small screens.
 */
export const useToggleSaved = (episode: Episode, podcast: Podcast) => {
    const { appStore, authStore, playlistStore } = useStores();
    const inList = playlistStore.inList(episode);

    const addToPlaylist = async () => {
        await playlistStore.add(episode, podcast);
        notifications.show({
            color: "teal",
            message: `${podcast.name} - ${episode.title} finns nu i din spellista`,
            autoClose: 2000,
        });
        if (!authStore.isSignedIn && !authStore.signedOutThisVisit && appStore.takeSignInNudge()) {
            notifications.show({
                message: (
                    <>
                        Sparad på den här enheten.{" "}
                        <Button
                            onClick={() =>
                                appStore.openSignIn("save", {
                                    message: `Logga in för att ha ${episode.title} i din spellista på alla dina enheter.`,
                                })
                            }
                            size="compact-xs"
                            variant="light">
                            Logga in
                        </Button>
                    </>
                ),
                autoClose: 8000,
            });
        }
    };

    const toggle = async () => {
        if (inList) {
            await playlistStore.remove(episode);
            return;
        }
        await addToPlaylist();
    };

    return { inList, toggle };
};

export const ToggleAddToPlaylistButton = observer(
    ({ episode, podcast, position = "none", removable = false }: ToggleAddToPlaylistButtonProps) => {
        const { inList, toggle } = useToggleSaved(episode, podcast);

        const icon = inList ? <BsCheck size={22} /> : <MdPlaylistAdd size={22} />;
        const label = inList ? (removable ? "Ta bort från spellistan" : "Sparad") : "Spara";

        return (
            <div className={position !== "none" ? styles[position] : undefined}>
                <ActionIcon
                    aria-label={
                        inList ? (removable ? "Ta bort från spellistan" : "Sparad i spellistan") : "Spara i spellistan"
                    }
                    color={inList ? "green" : "gray"}
                    disabled={inList && !removable}
                    onClick={toggle}
                    radius="xl"
                    size={44}
                    title={label}
                    variant="light">
                    {icon}
                </ActionIcon>
            </div>
        );
    }
);
